import React, { useState } from "react";
import { FiEye, FiCopy, FiFileText, FiDownload } from "react-icons/fi";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import api from "@/services/api";
import Loader from "../loader";

const translations = {
  en: {
    prompt_label: "Describe how you want the resume to look",
    prompt_placeholder: "E.g., 'Create a modern resume with a blue color scheme and emphasis on technical skills' or 'Design a minimalist resume suitable for creative industries'",
    generate_button: "Generate Resume",
    regenerate_button: "Regenerate Resume",
    generating: "Generating...",
    preview_button: "View",
    share_button: "Copy URL",
    pdf_button: "PDF",
    success_generated: "Resume generated successfully!",
    success_copied: "Share link copied to clipboard!",
    success_pdf: "PDF downloaded successfully!",
    error_generating: "Error generating resume",
    error_copying: "Error copying link",
    error_pdf: "Error generating PDF. Please try again.",
    no_resume: "No resume generated yet",
    last_generated: "Last generated",
  },
  fr: {
    prompt_label: "Décrivez comment vous souhaitez que le CV apparaisse",
    prompt_placeholder:
      "Ex: 'Créer un CV moderne avec une palette de couleurs bleues et l'accent sur les compétences techniques' ou 'Concevoir un CV minimaliste adapté aux industries créatives'",
    generate_button: "Générer le CV",
    regenerate_button: "Régénérer le CV",
    generating: "Génération...",
    preview_button: "Voir",
    share_button: "Copier URL",
    pdf_button: "PDF",
    success_generated: "CV généré avec succès!",
    success_copied: "Lien de partage copié dans le presse-papier!",
    success_pdf: "PDF téléchargé avec succès!",
    error_generating: "Erreur lors de la génération du CV",
    error_copying: "Erreur lors de la copie du lien",
    error_pdf: "Erreur lors de la génération du PDF. Veuillez réessayer.",
    no_resume: "Aucun CV généré pour le moment",
    last_generated: "Dernière génération",
  },
};

export default function AIResumeTab({ candidate }) {
  const { t } = useComponentTranslation(translations);
  const [prompt, setPrompt] = useState(candidate.generated_resume_prompt || "");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedHtml, setGeneratedHtml] = useState(candidate.generated_resume_html || "");
  const [shareUrl, setShareUrl] = useState(candidate.generated_resume_token ? `${window.location.origin}/resume/${candidate.generated_resume_token}` : "");

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a description for the resume");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await api.post(`/user/${candidate._id}/generate-resume`, {
        prompt: prompt.trim(),
      });

      if (!response.ok) {
        throw new Error(response.data?.message || "Failed to generate resume");
      }

      setGeneratedHtml(response.data.html);
      setShareUrl(response.data.shareUrl);
      toast.success(t("success_generated"));
    } catch (error) {
      console.error("Error generating resume:", error);
      toast.error(t("error_generating"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportPDF = async () => {
    if (!generatedHtml) {
      toast.error(t("no_resume"));
      return;
    }

    try {
      // Call backend API to generate PDF
      const response = await api.download(`/user/${candidate._id}/resume-pdf`);

      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${candidate.firstname}_${candidate.lastname}_Resume.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(t("success_pdf"));
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error(t("error_pdf"));
    }
  };

  const handlePreview = () => {
    if (!generatedHtml) {
      toast.error(t("no_resume"));
      return;
    }

    const previewWindow = window.open("", "_blank");
    previewWindow.document.write(generatedHtml);
    previewWindow.document.close();
  };

  const handleCopyShareLink = async () => {
    if (!shareUrl) {
      toast.error(t("no_resume"));
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("success_copied"));
    } catch (error) {
      toast.error(t("error_copying"));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6 h-full">
      {/* Left Column - Forms */}
      <div className="space-y-4 flex flex-col">
        {/* Generation Form */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t("prompt_label")}</label>
            <textarea
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows="10"
              placeholder={t("prompt_placeholder")}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isGenerating}
            />
          </div>

          <button
            onClick={handleGenerate}
            disabled={isGenerating || !prompt.trim()}
            className="w-full px-6 py-3 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isGenerating ? (
              <>
                <Loader />
                <span>{t("generating")}</span>
              </>
            ) : (
              <span>{generatedHtml ? t("regenerate_button") : t("generate_button")}</span>
            )}
          </button>
        </div>

        {/* Timestamp - Only show when resume exists */}
        {generatedHtml && candidate.generated_resume_created_at && (
          <div className="text-xs text-gray-500 text-center">
            {t("last_generated")}: {new Date(candidate.generated_resume_created_at).toLocaleString()}
          </div>
        )}
      </div>

      {/* Right Column - Preview */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900">Preview</h4>

          {/* Action Buttons - Only show when resume exists */}
          {generatedHtml && shareUrl && (
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreview}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <FiEye className="w-3.5 h-3.5" />
                <span>{t("preview_button")}</span>
              </button>

              <button
                onClick={handleExportPDF}
                className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <FiDownload className="w-3.5 h-3.5" />
                <span>{t("pdf_button")}</span>
              </button>

              <button
                onClick={handleCopyShareLink}
                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <FiCopy className="w-3.5 h-3.5" />
                <span>{t("share_button")}</span>
              </button>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-hidden">
          {generatedHtml ? (
            <iframe srcDoc={generatedHtml} className="w-full h-full border-0" title="Resume Preview" sandbox="allow-same-origin" />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6 text-center">
              <FiFileText className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-sm font-medium">No resume generated yet</p>
              <p className="text-xs mt-2">Generate a resume to see the preview here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
